import { useState } from "react";
import api from "../../api/account";
import { AuthUser } from "../../auth/types";

type DeleteAccountCardProps = {
  user: AuthUser;
  onSignOut: () => void;
};

export default function DeleteAccountCard({
  user,
  onSignOut,
}: DeleteAccountCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDeleteAccount = async () => {
    setError("");
    setLoading(true);

    try {
      await api.account.deleteAccount(user.email, user._pin);

      // Sign the user out once the account is gone.
      setShowConfirm(false);
      onSignOut();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Failed to delete account: ${message}. Please try again.`);
    }

    setLoading(false);
  };

  return (
    <>
      <div className="account-card account-card--wide account-card--danger">
        <div className="account-card-danger-header">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="24"
            height="24"
          >
            <path d="M12 9v2m0 4v2M7.26 4.26L5.82 2.82a1 1 0 0 0-1.41 1.41l1.44 1.44M16.74 4.26l1.44-1.44a1 1 0 1 1 1.41 1.41l-1.44 1.44M20.5 12a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0z" />
          </svg>
          <h2 className="account-card-title account-card-title--danger">
            Delete Account
          </h2>
        </div>
        <p className="account-card-description">
          Permanently delete your account and all associated data.
        </p>
        <button
          className="account-delete-btn"
          onClick={() => setShowConfirm(true)}
          disabled={loading}
        >
          Delete My Account
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="account-modal-overlay">
          <div className="account-modal">
            <div className="account-modal-header">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                width="32"
                height="32"
              >
                <path d="M12 9v6m0 6v-0.01M4.22 4.22a9 9 0 0 1 12.73 12.73m-12.73-12.73a9 9 0 0 0 12.73 12.73" />
              </svg>
              <h3 className="account-modal-title">Delete Your Account?</h3>
            </div>

            <div className="account-modal-body">
              <p className="account-modal-warning-title">
                This action cannot be undone.
              </p>

              <div className="account-modal-warning-box">
                {[
                  "Your data will be deleted permanently.",
                  "Data will be retained for 60 days as backup.",
                  "After 60 days, all data will be permanently deleted and non-recoverable.",
                  "You will be signed out of all devices and cannot log in again.",
                ].map((warning) => (
                  <div className="account-modal-warning-item" key={warning}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
                    </svg>
                    <span>{warning}</span>
                  </div>
                ))}
              </div>

              {error && <div className="account-modal-error">{error}</div>}
            </div>

            <div className="account-modal-footer">
              <button
                className="account-modal-btn account-modal-btn--secondary"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="account-modal-btn account-modal-btn--danger"
                onClick={handleDeleteAccount}
                disabled={loading}
              >
                {loading ? "Deleting…" : "Yes, Delete My Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
